import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuote } from "hooks/useQuote";
import { extractPlansFromBenefits, getSelectedBenefit } from "utils/mltaQuoteUtils";
import { ROUTES } from "routes/constants";
import type { PlanTier } from "types/Plan";

/**
 * Resolves the tier that should be selected, preferring a prior selection
 * (including one restored from a resumed application) when it maps to an
 * available plan, then defaulting to the first available plan (the single
 * option, or the first of several).
 */
const resolveTier = (availableTiers: PlanTier[], selectedTier?: PlanTier): PlanTier => {
    if (selectedTier && availableTiers.includes(selectedTier)) {
        return selectedTier;
    }
    return availableTiers[0] ?? "Economy";
};

/**
 * Encapsulates the data orchestration for the MLTA quote results page:
 * derives plan options and pricing from the stored quote, keeps the selected
 * tier in sync, and persists selection changes back to the store.
 *
 * @returns Plan options, pricing, selection state, and handlers for rendering.
 */
export const useMLTAQuoteResults = () => {
    // ========== Hooks ==========
    const navigate = useNavigate();
    const { quoteData, saveQuote, storeQuote } = useQuote();

    // ========== Computed Values ==========
    const { primaryPlan, secondaryPlan } = useMemo(
        () => extractPlansFromBenefits(quoteData?.mltaBenefits ?? []),
        [quoteData?.mltaBenefits]
    );

    const hasValidBenefits = Boolean(quoteData?.mltaBenefits && quoteData.mltaBenefits.length > 0);

    const availableTiers = useMemo(
        () => [primaryPlan?.id, secondaryPlan?.id].filter((id): id is PlanTier => Boolean(id)),
        [primaryPlan?.id, secondaryPlan?.id]
    );

    const defaultTier = useMemo(
        () => resolveTier(availableTiers, quoteData?.selectedTier),
        [availableTiers, quoteData?.selectedTier]
    );

    // ========== State ==========
    const [selectedTier, setSelectedTier] = useState<PlanTier>(defaultTier);
    const [includeFamilyCoverage, setIncludeFamilyCoverage] = useState(quoteData?.selectedCoverageType === "Family");

    const quoteDataRef = useRef(quoteData);
    quoteDataRef.current = quoteData;

    // ========== Computed Values (Price) ==========
    const selectedBenefit = useMemo(
        () => getSelectedBenefit(quoteData?.mltaBenefits ?? [], selectedTier),
        [quoteData?.mltaBenefits, selectedTier]
    );

    const familyPriceDelta = useMemo(() => {
        if (!selectedBenefit) {
            return 0;
        }
        return selectedBenefit.family - selectedBenefit.individual;
    }, [selectedBenefit]);

    const monthlyPrice = useMemo(() => {
        if (!selectedBenefit) {
            return 0;
        }
        return includeFamilyCoverage ? selectedBenefit.family : selectedBenefit.individual;
    }, [selectedBenefit, includeFamilyCoverage]);

    // ========== Effects ==========
    useEffect(() => {
        if (!hasValidBenefits) {
            console.error("MLTAQuoteResultsPage: Missing quote benefits data");
            navigate(ROUTES.SOMETHING_WENT_WRONG);
        }
    }, [hasValidBenefits, navigate]);

    useEffect(() => {
        setSelectedTier(resolveTier(availableTiers, quoteData?.selectedTier));
    }, [availableTiers, quoteData?.selectedTier]);

    useEffect(() => {
        const currentQuoteData = quoteDataRef.current;
        if (!currentQuoteData?.mltaBenefits || monthlyPrice === 0) {
            return;
        }

        saveQuote({
            ...currentQuoteData,
            selectedTier: selectedTier,
            selectedCoverageType: includeFamilyCoverage ? "Family" : "Individual",
            selectedCoveragePremium: monthlyPrice,
            selectedCoverageAmount: selectedBenefit?.common,
        });
    }, [selectedTier, includeFamilyCoverage, monthlyPrice, selectedBenefit, saveQuote]);

    useEffect(() => {
        if (!quoteData?.mltaBenefits || !quoteData.selectedTier || !quoteData.selectedCoveragePremium) {
            return;
        }
        storeQuote();
    }, [
        quoteData?.mltaBenefits,
        quoteData?.selectedTier,
        quoteData?.selectedCoverageType,
        quoteData?.selectedCoveragePremium,
        storeQuote,
    ]);

    // ========== Event Handlers ==========
    const handlePlanSelect = (planId: PlanTier) => {
        setSelectedTier(planId);
    };

    const handleClaimReward = () => {
        if (!quoteData?.mltaBenefits) {
            return;
        }
        navigate(ROUTES.APPLICATION.CONTACT);
    };

    return {
        primaryPlan,
        secondaryPlan,
        hasValidBenefits,
        selectedTier,
        includeFamilyCoverage,
        setIncludeFamilyCoverage,
        familyPriceDelta,
        monthlyPrice,
        handlePlanSelect,
        handleClaimReward,
    };
};
